import classNames from "classnames";
import React, { memo, ReactElement } from "react";

interface LoadingStatusProps {
  /** Whether content is currently loading */
  loading: boolean;
  /** Message to announce to screen readers */
  message?: string;
  /** Additional CSS classes */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Whether to show visual loading indicator */
  showVisual?: boolean;
  /** Children to render (e.g., spinner) */
  children?: React.ReactNode;
}

/**
 * LoadingStatus component provides accessible loading announcements for screen readers.
 *
 * **Accessibility Features:**
 * - Uses role="status" for live region
 * - aria-live="polite" ensures announcements don't interrupt user
 * - aria-busy indicates loading state
 * - Screen reader only text via sr-only class
 *
 * @example
 * ```tsx
 * <LoadingStatus
 *   loading={isLoading}
 *   message="Loading available appointment times"
 * >
 *   <Spinner />
 * </LoadingStatus>
 * ```
 */
export const LoadingStatus = memo(function LoadingStatus({
  loading,
  message = "",
  className = "",
  style,
  showVisual = true,
  children
}: LoadingStatusProps): ReactElement | null {
  if (!loading) {
    return null;
  }

  const containerClasses = classNames("flex items-center gap-sm", className);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={containerClasses}
      style={style}
    >
      {/* Screen reader only text */}
      <span className="sr-only">{message}</span>

      {/* Visual indicator */}
      {showVisual && children}
    </div>
  );
});

export default LoadingStatus;
